import { useMutation } from "@apollo/client";
import { PlusIcon } from "@heroicons/react/solid";
import { uniqueId } from "lodash";
import uniqBy from "lodash/uniqBy";
import moment from "moment";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import { delay, tempCacheIdPrefix, topicState } from "@helpers/constants";

import createOrUpdateTopicMutation from "../graphql/create-or-update-topic-mutation";
import TopicNodeFragment from "../graphql/topic-node-fragment";
import { focusOnTopicTitleInput } from "../helpers";

const AddTopicHover = ({
  meeting,
  afterTopicId,
  beforeTopicId,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  afterTopicId?: number;
  beforeTopicId?: number;
}) => {
  const currentUser = currentUserVar();
  const [createTopic] = useMutation(createOrUpdateTopicMutation);

  const handleClickAdd = () => {
    const tempId = uniqueId(`${tempCacheIdPrefix}-topic`);
    const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
    const topicData = {
      id: tempId,
      title: "New Topic",
      description: "",
      includesIndividualNotes: isFormalOneonone,
      includesIndividualNotesForCurrentUser: isFormalOneonone,
      includesSharedNotes: !isFormalOneonone,
      discussionNotes: null,
      isMandatory: false,
      canUpdate: { __typename: "PermissionNode", permission: true },
      canDelete: { __typename: "PermissionNode", permission: true },
      created: moment().format(),
      state: topicState.open,
      eventChannelName: `topic-${tempId}`,
      creator: currentUser,
      assignee: currentUser,
      previousTopic: null,
      linkedTemplateTopic: null,
      notesRequirement: null,
      comments: { totalCount: 0, __typename: "CommentNodeConnection" },
      individualNotes: {
        edges: [],
        __typename: "IndividualNoteNodeConnection",
      },
      copiedFrom: null,
      __typename: "TopicNode",
    };
    createTopic({
      variables: {
        meetingId: meeting.id,
        meetingGroupId: meeting.meetingGroup?.id,
        title: topicData.title,
        afterTopicId,
        beforeTopicId,
        includesIndividualNotes: isFormalOneonone,
        includesSharedNotes: !isFormalOneonone,
      },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: topicData,
          __typename: "CreateOrUpdateTopicMutation",
        },
      },
      onCompleted: ({ createOrUpdateTopic }) => {
        setTimeout(() => {
          focusOnTopicTitleInput(createOrUpdateTopic.topic.id, meeting.id);
        }, delay.waitTillFocus);
      },
      update(cache, response) {
        const { topic } = response.data.createOrUpdateTopic;
        const createdTopicCacheNode = cache.writeFragment({
          data: topic,
          fragment: TopicNodeFragment,
          fragmentName: "TopicNodeNewPageFragment",
          variables: { meetingGroupId: meeting.meetingGroup?.id },
        });
        const meetingCacheId = cache.identify(meeting);
        cache.modify({
          id: meetingCacheId,
          fields: {
            topics(cachedTopics) {
              const afterTopicRef = afterTopicId
                ? cache.identify({
                    __typename: "TopicNode",
                    id: afterTopicId,
                  })
                : null;
              const beforeTopicRef = beforeTopicId
                ? cache.identify({
                    __typename: "TopicNode",
                    id: beforeTopicId,
                  })
                : null;
              const mergedEdges = afterTopicRef
                ? cachedTopics.edges.reduce((memo: any[], cachedTopic: any) => {
                    if (cachedTopic.node.__ref === afterTopicRef) {
                      return [
                        ...memo,
                        cachedTopic,
                        {
                          node: createdTopicCacheNode,
                          __typename: "TopicNodeEdge",
                        },
                      ];
                    }
                    return [...memo, cachedTopic];
                  }, [])
                : beforeTopicRef
                  ? cachedTopics.edges.reduce(
                      (memo: any[], cachedTopic: any) => {
                        if (cachedTopic.node.__ref === beforeTopicRef) {
                          return [
                            ...memo,
                            {
                              node: createdTopicCacheNode,
                              __typename: "TopicNodeEdge",
                            },
                            cachedTopic,
                          ];
                        }
                        return [...memo, cachedTopic];
                      },
                      [],
                    )
                  : [
                      ...cachedTopics.edges,
                      {
                        node: createdTopicCacheNode,
                        __typename: "TopicNodeEdge",
                      },
                    ];
              const uniqueEdges = uniqBy(mergedEdges, ({ node }) => node.__ref);
              return { edges: uniqueEdges };
            },
          },
        });
      },
    });
  };

  if (!meeting.canUpdate?.permission) return null;

  return (
    <div className="relative bg-white h-px py-4 w-full group/add-topic">
      <div className="h-px bg-gray-200" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover/add-topic:opacity-100">
        <Button
          onClick={handleClickAdd}
          size={ButtonSize.medium}
          className="gap-1 tracking-tight"
        >
          <PlusIcon className="w-4 h-4" />
          Add Topic
        </Button>
      </div>
    </div>
  );
};

export default AddTopicHover;
